// 📄 pages/Proyectos/components/SolucionPlaceholder.tsx
// Contenido informativo genérico para una solución sin dashboard propio
// (SLP, Sourcing, Buying, Contracts, CIG). Reutiliza el mismo contenido
// "¿qué es esto?" que antes vivía en las páginas sueltas de cada módulo.
//
// Orden estandarizado (igual en todas las soluciones que usan esta
// vista): Header/Introducción → Integración (Configuraciones Generales)
// → Canales de Integración específicos del módulo (si el módulo ya los
// tiene definidos, ver integrationChannels.ts) → sección secundaria al
// final (aviso de "todavía no implementado" SOLO si el módulo no tiene
// canales propios — dejar de mostrarlo en cuanto los tiene sería
// engañoso, ya hay algo real que configurar).
import React from 'react';
import { Link } from 'react-router-dom';
import { SolucionMeta } from '../solucionesConfig.ts';
import { useProject } from '../../../context/ProjectContext.tsx';
import { getCanalesDeSolucion } from '../integrationChannels.ts';
import IntegrationSection from './IntegrationSection.tsx';
import GuiaIntegracionCard from './GuiaIntegracionCard.tsx';
import ArrangementsSection from './ArrangementsSection.tsx';
import { esErpCloud } from '../../views/AribaCommerce/Integracion/useGeneralConfig.ts';
import { SolucionConArrangements } from '../../../services/arrangementService.ts';

// Sub-fase K7: solo estas 3 solucionan (de las 5 que usan este placeholder)
// tienen Arrangements por ahora — buying/cig todavía no tienen ningún
// modelo de integración definido (canales.length === 0 siempre), así que
// no habría nada que referenciar.
const SOLUCIONES_CON_ARRANGEMENTS: string[] = ['slp', 'sourcing', 'contracts'];

export default function SolucionPlaceholder({ slug, icon, tagline, sectionTitle, description, cards, accentClass }: SolucionMeta) {
  const { project, projectId } = useProject();
  const canales = getCanalesDeSolucion(slug);
  const mostrarArrangements = SOLUCIONES_CON_ARRANGEMENTS.includes(slug) && esErpCloud(project?.erpTipo);

  return (
    <div className="max-w-4xl space-y-6">
      {/* Header / Introducción */}
      <div className="flex items-start gap-4 bg-white/5 border border-white/10 rounded-2xl p-6">
        <div className="text-4xl">{icon}</div>
        <div>
          <p className="text-gray-300">{tagline}</p>
        </div>
      </div>

      {/* Guía de Integración Estándar — arriba de todo, es el "empezá por
          acá" antes de entrar a configurar canal por canal (ver
          GuiaIntegracionCard.tsx: no renderiza nada si el Admin todavía no
          cargó una guía para esta combinación Solución+ERP). */}
      {projectId && <GuiaIntegracionCard projectId={projectId} solucion={slug} />}

      <div className="bg-white/5 border border-white/10 rounded-2xl p-8">
        <h2 className={`text-xl font-semibold mb-4 ${accentClass}`}>{sectionTitle}</h2>
        <p className="text-gray-300 leading-relaxed mb-8">{description}</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {cards.map((card) => (
            <div key={card.title} className="bg-white/5 p-6 rounded-2xl border border-white/5">
              <h3 className="text-white font-medium mb-2">{card.title}</h3>
              <p className="text-sm text-gray-400">{card.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Sección 1: Integración — Configuraciones Generales */}
      {projectId && <IntegrationSection projectId={projectId} />}

      {/* Sección 2: Canales de Integración específicos del módulo */}
      {projectId && canales.length > 0 && (
        <div>
          <h3 className="text-xl mb-4 font-medium text-green-200">Canales de Integración</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {canales.map((canal) => (
              <Link
                key={canal.slug}
                to={`/proyecto/${projectId}/integracion/${slug}/${canal.slug}`}
                className="block bg-white/5 hover:bg-white/10 border border-white/10 rounded-2xl p-5 transition-all duration-200"
              >
                <div className="text-blue-400 mb-2 text-xl leading-none">{canal.icon}</div>
                <h4 className="text-lg font-semibold text-white">{canal.label}</h4>
                <p className="text-sm text-gray-400 mt-1">{canal.description}</p>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Sección 2.5: Communication Arrangements (K7) — solo en proyectos Cloud */}
      {mostrarArrangements && projectId && (
        <ArrangementsSection projectId={projectId} solucion={slug as SolucionConArrangements} />
      )}

      {/* Sección 3: secundaria — solo si el módulo todavía no tiene canales propios definidos */}
      {canales.length === 0 && (
        <div className="bg-amber-500/5 border border-amber-500/20 rounded-2xl p-5 text-sm text-amber-200/80">
          El checklist detallado de configuración para esta solución todavía no está implementado — por ahora esta es una vista informativa.
        </div>
      )}
    </div>
  );
}
